import { Fragment } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePipeline, type StageKey, type StageState } from '../lib/pipeline.js';
import { useT } from '../lib/i18n.js';

/**
 * Die Pipeline-Leiste (README §6.1): sechs Stufen, live aus den echten
 * Daten. Planen und Erstellen folgen der Phase, die der Lauf selbst meldet,
 * Prüfen den offenen Freigaben, Rendern und Veröffentlichen den Posts.
 * Die Leiste aktualisiert sich über dieselben SSE-Ereignisse wie der Rest.
 */
export function PipelineBar({ projectId, compact }: { projectId: string; compact?: boolean }) {
  const t = useT();
  const navigate = useNavigate();
  const { stages } = usePipeline(projectId);
  const here = stageOfPath(useLocation().pathname, projectId);

  return (
    <nav className="pipeline" aria-label={t('Pipeline')}>
      {!compact && <span className="pipeline-label">{t('Pipeline')}</span>}
      {stages.map((s, i) => (
        <Fragment key={s.key}>
          {i > 0 && <span className="pipeline-sep" aria-hidden>›</span>}
          <button
            type="button"
            className={`pipe-step pipe-${s.state} ${here === s.key ? 'pipe-here' : ''}`}
            onClick={() => navigate(s.to)}
            title={`${s.label}: ${s.sub}`}
            aria-current={here === s.key ? 'step' : undefined}
          >
            {here === s.key && <span className="pipe-here-pill">{t('Du bist hier')}</span>}
            <span className={`pipe-dot ${s.state === 'live' ? 'ring-live' : ''}`}>
              {glyph(s.state, s.n)}
              {!!s.waiting && <span className="pipe-wait" aria-label={t('{n} warten auf dich', { n: s.waiting })}>{s.waiting > 9 ? '9+' : s.waiting}</span>}
            </span>
            <span className="col" style={{ minWidth: 0 }}>
              <span className="pipe-label">{s.label}</span>
              <span className="pipe-sub">{s.sub}</span>
            </span>
          </button>
        </Fragment>
      ))}
      {/* Was die drei Zeichen bedeuten -- ohne Legende rät man beim ersten Mal. */}
      {!compact && (
        <span className="pipe-legend" aria-hidden>
          <span><i className="pipe-key pipe-key-live" /> {t('Agent arbeitet')}</span>
          <span><i className="pipe-key pipe-key-here" /> {t('Du bist hier')}</span>
          <span><i className="pipe-key pipe-key-wait" /> {t('wartet auf dich')}</span>
        </span>
      )}
    </nav>
  );
}

/*
 * Die Nummer bleibt stehen, auch wenn etwas auf dich wartet: Ein „!" statt
 * der Zahl nahm der Leiste die Reihenfolge -- und genau die soll man an ihr
 * ablesen können.
 */
function glyph(state: StageState, n: number): string {
  if (state === 'done') return '✓';
  return String(n);
}

/**
 * Welche Stufe gehört zur Seite, auf der man gerade steht?
 *
 * Rendern und Veröffentlichen teilen sich den Kalender; markiert wird dort
 * „Veröffentlichen", weil die Seite genau das zeigt.
 */
function stageOfPath(pathname: string, projectId: string): StageKey | null {
  const rest = pathname.split(`/projects/${projectId}`)[1] ?? '';
  if (rest.startsWith('/run') || rest.startsWith('/build')) return 'create';
  if (rest.startsWith('/review')) return 'review';
  if (rest.startsWith('/publish')) return 'publish';
  if (rest.startsWith('/results')) return 'learn';
  return null;
}
